import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Copy, MessageCircle, QrCode, X } from "lucide-react";
import { brl, maskPhone } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderModal({ service, pricing, user, close, onOrdered }) {
  const pr = pricing?.services?.[service.id];
  const isCapas = service.id === "capas";
  const monthly = service.id === "beta" || service.id === "plus";
  const suffix = monthly ? "/mês" : "";

  const [option, setOption] = useState("1");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [order, setOrder] = useState(null);
  const [pix, setPix] = useState(null);
  const [copied, setCopied] = useState(false);

  const capasOpt = isCapas ? pr?.options?.find((o) => o.id === option) : null;
  const base = isCapas ? capasOpt?.price : pr?.base;
  const final = isCapas ? capasOpt?.price : pr?.final;
  const discount = isCapas ? 0 : pr?.discount || 0;
  const benefit = pr?.benefit;

  const canSubmit = confirmed && !busy &&
    (user ? true : name.trim().length >= 2 && phone.replace(/\D/g, "").length >= 10);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          service_id: service.id,
          option: isCapas ? option : null,
          notes: notes.trim() || null,
          client_name: user ? null : name.trim(),
          client_phone: user ? null : phone,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Não foi possível concluir o pedido agora.");
      setOrder(data);
      setStep(2);
      onOrdered?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const choose = async (method) => {
    await fetch(`${API}/orders/${order.order_id}/payment`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ order_token: order.order_token, method }),
    }).catch(() => {});
  };

  const chooseWhatsApp = async () => {
    await choose("whatsapp");
    window.open(order.whatsapp_url, "_blank");
  };

  const choosePix = async () => {
    setBusy(true);
    setError("");
    try {
      await choose("pix");
      const res = await fetch(`${API}/orders/${order.order_id}/pix?token=${order.order_token}`, { credentials: "include" });
      if (!res.ok) throw new Error("pix");
      setPix(await res.json());
      setStep(3);
    } catch {
      setError("Não foi possível gerar o QR Code agora. Tente pelo WhatsApp.");
    } finally {
      setBusy(false);
    }
  };

  const copyPix = async () => {
    try {
      await navigator.clipboard.writeText(pix.payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard indisponível */ }
  };

  return (
    <motion.div className="modal-backdrop" onMouseDown={close}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
      <motion.div className="quote-modal order-modal" onMouseDown={(e) => e.stopPropagation()} data-testid="order-modal"
        initial={{ opacity: 0, y: 44, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.98 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>
        <button className="modal-close" onClick={close} data-testid="close-order-modal" aria-label="Fechar"><X /></button>

        {step === 1 && (
          <>
            <div className="eyebrow"><i /> CONTRATAÇÃO</div>
            <h2 data-testid="order-service-name">{service.name}</h2>
            <p className="modal-intro">{service.modalIntro}</p>

            {isCapas && pr?.options && (
              <div className="qty-row" data-testid="order-capas-options">
                {pr.options.map((o) => (
                  <button type="button" key={o.id} className={`qty-opt ${option === o.id ? "is-active" : ""}`}
                    onClick={() => setOption(o.id)} data-testid={`order-capas-option-${o.id}`}>
                    {o.label}
                    <b>{o.promo ? <><s>{brl(o.original_price)}</s> {brl(o.price)}</> : brl(o.price)}</b>
                    {o.promo && <span className="qty-promo">PROMOÇÃO DE DOMINGO</span>}
                  </button>
                ))}
              </div>
            )}

            <div className="order-price-box" data-testid="order-price-box">
              {discount > 0 ? (
                <>
                  <div className="op-row"><span>Valor original</span><s>{brl(base)}{suffix}</s></div>
                  <div className="op-row gold"><span>Benefício de primeiro login</span><b>− {brl(discount)}</b></div>
                  <div className="op-row total"><span>Valor final</span><strong data-testid="order-final-price">{brl(final)}{suffix}</strong></div>
                  <div className="op-save">Você economiza {brl(discount)}</div>
                </>
              ) : (
                <div className="op-row total"><span>Valor</span><strong data-testid="order-final-price">{brl(final)}{suffix}</strong></div>
              )}
            </div>

            {benefit && (
              <p className="order-benefit" data-testid="order-benefit-note">
                Você está logado e recebeu 1 semana deste serviço gratuitamente como benefício de boas-vindas.
              </p>
            )}

            {!user && (
              <div className="order-fields">
                <label>Seu nome *
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Como podemos te chamar?"
                    data-testid="order-name-input" maxLength={120} />
                </label>
                <label>Seu WhatsApp *
                  <input value={phone} onChange={(e) => setPhone(maskPhone(e.target.value))} placeholder="(21) 99999-9999"
                    inputMode="tel" data-testid="order-phone-input" />
                </label>
              </div>
            )}

            <label className="order-notes">Observações (opcional)
              <textarea value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                placeholder="Algum detalhe importante sobre o seu pedido?" data-testid="order-notes-input" />
            </label>

            <label className="order-check" data-testid="order-confirm-check">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              <span>Confirmo a contratação deste serviço nas condições acima.</span>
            </label>

            {error && <p className="field-error" data-testid="order-error">{error}</p>}
            <button className="button button-primary full" onClick={submit} disabled={!canSubmit} data-testid="order-confirm-button">
              {busy ? "Confirmando..." : "Confirmar pedido"} <ArrowUpRight size={17} />
            </button>
          </>
        )}

        {step === 2 && order && (
          <div data-testid="order-payment-step">
            <span className="success-ring"><Check size={26} /></span>
            <div className="eyebrow"><i /> PEDIDO REGISTRADO</div>
            <h2>Pedido<br /><em>confirmado.</em></h2>
            <p className="modal-intro">
              {order.service_name} — valor final de <b className="gold-text">{brl(order.final_price)}{suffix}</b>.
              Escolha como prefere pagar:
            </p>
            <button className="button button-primary full" onClick={chooseWhatsApp} data-testid="order-whatsapp-button">
              Continuar pagamento via WhatsApp <MessageCircle size={17} />
            </button>
            <button className="btn-ghost full ghost-lg" onClick={choosePix} disabled={busy} data-testid="order-pix-button">
              {busy ? "Gerando..." : "Gerar QR Code copiável"} <QrCode size={17} />
            </button>
            <p className="order-note">Após realizar o pagamento, envie o comprovante pelo WhatsApp para confirmar seu pedido.</p>
            {error && <p className="field-error" data-testid="order-error">{error}</p>}
          </div>
        )}

        {step === 3 && pix && (
          <div data-testid="order-pix-step">
            <div className="eyebrow"><i /> PAGAMENTO VIA PIX</div>
            <h2>QR Code<br /><em>{pix.amount_label}{suffix}</em></h2>
            <div className="pix-box">
              <img src={pix.qr} alt={`QR Code PIX de ${pix.amount_label}`} data-testid="pix-qr-image" />
              <button className="button button-primary full" onClick={copyPix} data-testid="pix-copy-button">
                {copied ? "Código copiado!" : "Copiar código PIX"} <Copy size={16} />
              </button>
            </div>
            <p className="order-note">Após realizar o pagamento, envie o comprovante pelo WhatsApp para confirmar seu pedido.</p>
            <button className="text-button centered" onClick={chooseWhatsApp} data-testid="pix-whatsapp-fallback">
              Prefiro continuar pelo WhatsApp
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
